import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import type { ErrorLike } from '@apollo/client'
import clsx from 'clsx'

import {
  Flex,
  IconButton,
  SendHorizontalIcon,
  Skeleton,
  Text,
  UserRoundPlusIcon,
} from '@buffer-mono/popcorn'

import { getTimeSeriesSummary } from '../MetricChart/getTimeSeriesSummary'
import {
  buildChartSeries,
  computeNetNewSeries,
} from '../MetricChart/buildChartSeries'
import {
  isViewMode,
  MetricUnit,
  SummaryMode,
  ViewMode,
} from '../MetricChart/types'
import type { MetricSeries } from '../MetricChart/types'
import type { ChartAnnotation } from '../charts'
import { useExportChartAsPost } from '../../hooks/useExportChartAsPost'
import { captureChartAsBlob } from '../../chartToImage'
import { useTrackInsightsItemClicked } from '../../hooks/useTrackInsightsItemClicked'
import {
  formatDateRange,
  formatInsightsDateRange,
  formatNumber,
  getComparisonLengthNotice,
  getNoDataDescription,
} from '../../utils'
import {
  useInsightsComparison,
  useInsightsDateRange,
} from '../../context/InsightsPageContext'
import { useRegisterSnapshots } from '../../context'
import { ChartErrorState } from '../ChartErrorState'
import { FollowerThresholdEmptyState } from '../FollowerThresholdEmptyState'
import { NeverSyncedEmptyState } from '../NeverSyncedEmptyState'
import { NoDataEmptyState } from '../NoDataEmptyState'
import { SectionCard } from '../SectionCard'
import { SegmentedDropdown } from '../ResponsiveSegmentedControl'

import { BenchmarkSummaryPanel } from './BenchmarkSummaryPanel'
import { buildMetricPaletteIndex, metricColorToken } from './chartSeriesInputs'
import { ContentImpactChart } from './ContentImpactChart'
import { MetricLineChart } from './MetricLineChart'
import { PostsColumnChart } from './PostsColumnChart'
import { InsightNotice } from './InsightNotice'
import { useInsightMessage } from './useInsightMessage'
import { NarrativeTabBar } from './NarrativeTabBar'
import { PeriodToggle, PERIOD_DROPDOWN_OPTIONS } from './PeriodToggle'
import { SummaryPanel } from './SummaryPanel'
import type { SummaryItem } from './SummaryPanel'
import { getDefaultNarrativeViewKey, NARRATIVE_VIEWS } from './narrativeViews'
import type { NarrativeViewKey } from './narrativeViews'
import { buildMetricsViewSnapshot } from './metricsSnapshotViews'
import sectionButtonStyles from '../sectionActionButton.module.css'
import styles from './MetricsNarrativeChart.module.css'

export interface ChannelInfo {
  channelIds?: string[]
  channelName?: string
  /** Raw service id (e.g. "instagram") — drives the share snapshot's icon. */
  channelService?: string
  /** Channel avatar URL — used by the share snapshot header. */
  channelAvatarUrl?: string
}

interface MetricsNarrativeChartProps {
  metrics: MetricSeries[]
  loading: boolean
  refreshing?: boolean
  error?: ErrorLike
  channelInfo?: ChannelInfo
  /** When false, hides the "Share as Post" button (sharing opens the composer). */
  canCreatePosts?: boolean
  hasNeverSynced?: boolean
  /**
   * When true, the chart's no-data state explains Meta's Facebook Page follower
   * threshold (channel insights need 100 followers) instead of the generic
   * "no data in range" notice. @see CORE-6425
   */
  belowFollowerThreshold?: boolean
  /**
   * No posts were published on the channel in the period. Turns a flat metric's
   * "held steady" observation into a prompt to review Takeaways. @see CORE-6444
   */
  isInactive?: boolean
  /** Scrolls to the Takeaways section — wired to the inactive prompt's link. */
  onReviewTakeaways?: () => void
  /** Whether the selected period includes the past week. Gates the inactive
   *  prompt's actionable "review your takeaways" copy + link vs a plain
   *  past-tense note for older periods. @see CORE-6444 */
  isCurrentPeriod?: boolean
  /**
   * Whether this channel's network reports profile views. When false, the
   * Visibility narrative (which plots views) is hidden. Defaults to true.
   */
  supportsViews?: boolean
  /**
   * Whether this channel's network reports followers. When false, every
   * narrative that plots followers (Content Impact, Audience Growth,
   * Visibility) is hidden. Defaults to true.
   */
  supportsFollowers?: boolean
  /** Fixed start of the 12-month benchmark window. Independent of the date
   * picker — benchmark history doesn't respect date filters. */
  benchmarkStartDate?: Date
  /** Fixed end of the 12-month benchmark window. */
  benchmarkEndDate?: Date
  /** Latest channelPercentileRank (0–100) from the benchmark API. */
  benchmarkPercentileRank?: number | null
  /** When true, shows a warning that tag filters don't apply to this chart. */
  hasTagFilter?: boolean
  /** When true, shows the same warning for the post type filter, which narrows
   * posts and so cannot narrow a channel-level series either. */
  hasPostTypeFilter?: boolean
  /** Timeline flag annotations for the narrative line view (e.g. the channel's
   * data-collection start). Rendered only on views that plot a collected-analytics
   * series (followers, reach, views) — the metrics they explain. */
  annotations?: ChartAnnotation[]
  /** Day-aligned x-axis ticks so date labels line up under annotation flags. */
  xAxisTicks?: number[]
}

/**
 * Sentence-cased warning naming the filters a chart ignores, e.g.
 * "Tag filters don't apply to this chart" or "Date, tag and post type filters
 * don't apply to this chart".
 *
 * Exported for its test: the copy is the contract, and the benchmark view's
 * wording must not drift when a filter is added.
 */
export function inapplicableFiltersNotice(names: string[]): string {
  const list =
    names.length > 2
      ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
      : names.join(' and ')
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} filters don't apply to this chart`
}

export function MetricsNarrativeChart({
  metrics,
  loading,
  refreshing = false,
  error,
  channelInfo,
  canCreatePosts = true,
  hasNeverSynced,
  belowFollowerThreshold = false,
  isInactive = false,
  onReviewTakeaways,
  isCurrentPeriod = true,
  supportsViews = true,
  supportsFollowers = true,
  benchmarkStartDate,
  benchmarkEndDate,
  benchmarkPercentileRank = null,
  hasTagFilter,
  hasPostTypeFilter,
  annotations,
  xAxisTicks,
}: MetricsNarrativeChartProps): JSX.Element {
  const { channelIds, channelName, channelService, channelAvatarUrl } =
    channelInfo ?? {}
  const { startDate, endDate, selectedDateRange } = useInsightsDateRange()
  const { comparisonStartDate, comparisonEndDate } = useInsightsComparison()
  const trackInsightsItemClicked = useTrackInsightsItemClicked()

  const hasFollowers = useMemo(
    () => metrics.some((m) => m.key === 'followers'),
    [metrics],
  )
  const defaultViewKey = getDefaultNarrativeViewKey(hasFollowers)

  const [userSelectedViewKey, setUserSelectedViewKey] =
    useState<NarrativeViewKey | null>(null)
  const selectedViewKey = userSelectedViewKey ?? defaultViewKey
  // Which single metric the "Posts" dropdown tab is currently showing.
  const [singleMetricKey, setSingleMetricKey] = useState<string>('posts')
  // The user's explicit period selection. Defaults to "This Period" so the chart
  // opens on the current period; the user opts into Comparison/Both. The
  // *effective* view mode is derived below (falling back to primary when there's
  // no comparison data).
  const [selectedViewMode, setSelectedViewMode] = useState<ViewMode>(
    ViewMode.Primary,
  )

  // Filter the narrative views to those that apply to this channel:
  // - follower-growth-rate needs benchmark data (LinkedIn-only today).
  // - visibility plots profile views, which some networks don't report.
  // - followers-based views need a network that reports followers.
  const availableViews = useMemo(() => {
    const hasBenchmarkData = metrics.some((m) => m.key.startsWith('benchmark-'))
    return NARRATIVE_VIEWS.filter((view) => {
      if (view.key === 'follower-growth-rate') return hasBenchmarkData
      if (!supportsFollowers && view.metricKeys.includes('followers')) {
        return false
      }
      if (view.key === 'visibility') return supportsViews
      return true
    })
  }, [metrics, supportsViews, supportsFollowers])

  // Reset per channel so a view picked on one channel doesn't carry to the next.
  const primaryChannelId = channelIds?.[0]
  useEffect(() => {
    setUserSelectedViewKey(null)
  }, [primaryChannelId])

  useEffect(() => {
    if (
      userSelectedViewKey &&
      !availableViews.some((v) => v.key === userSelectedViewKey)
    ) {
      setUserSelectedViewKey(null)
    }
  }, [availableViews, userSelectedViewKey])

  const selectedView =
    availableViews.find((v) => v.key === selectedViewKey) ?? availableViews[0]

  // Extend the incoming metrics with derived series (e.g. net-new followers)
  // so views can reference keys that don't exist in the raw API response.
  const metricsWithDerived = useMemo(() => {
    const followersMetric = metrics.find((m) => m.key === 'followers')
    if (!followersMetric) return metrics
    // Net new followers gets its own person-add icon rather than inheriting the
    // Followers (audience) icon, so it reads distinctly in the metric dropdown.
    const netNew = computeNetNewSeries(followersMetric)
    return [...metrics, { ...netNew, icon: <UserRoundPlusIcon size="small" /> }]
  }, [metrics])

  // Options for the "Posts" dropdown — all non-benchmark metrics.
  const singleMetricOptions = useMemo(
    () => metricsWithDerived.filter((m) => !m.key.startsWith('benchmark-')),
    [metricsWithDerived],
  )

  // If the selected single metric disappears (channel switch), reset to first available.
  useEffect(() => {
    if (
      singleMetricOptions.length > 0 &&
      !singleMetricOptions.some((m) => m.key === singleMetricKey)
    ) {
      setSingleMetricKey(singleMetricOptions[0].key)
    }
  }, [singleMetricOptions, singleMetricKey])

  const singleMetricTitle = useMemo(
    () =>
      metricsWithDerived.find((m) => m.key === singleMetricKey)?.title ??
      'Posts',
    [metricsWithDerived, singleMetricKey],
  )

  // Filter down to the metrics relevant to the current view, preserving key
  // order so the first metric gets the left Y-axis.
  // The 'posts' view uses the dynamically selected singleMetricKey.
  const visibleMetrics = useMemo(() => {
    const keys =
      selectedViewKey === 'posts'
        ? [singleMetricKey]
        : (selectedView?.metricKeys ?? [])
    return keys
      .map((key) => metricsWithDerived.find((m) => m.key === key))
      .filter((m): m is MetricSeries => m != null)
  }, [selectedView, selectedViewKey, singleMetricKey, metricsWithDerived])

  const hasAnyComparison = useMemo(
    () =>
      visibleMetrics.some(
        (m) => m.comparisonData != null && m.comparisonData.length > 0,
      ),
    [visibleMetrics],
  )

  // Effective view mode, derived (not stored): fall back to 'primary' when
  // there's no comparison data, and restore the user's selection when it
  // returns. Deriving rather than reconciling via setState avoids a mount-then-
  // flip on load — that mid-mount series change hands recharts a new data
  // reference, which restarts the line's entry animation and makes it render as
  // a briefly broken/segmented line for ~1.5s until it settles.
  const viewMode = hasAnyComparison ? selectedViewMode : ViewMode.Primary

  // Guarded setter for the collapsed period dropdown (its onChange is untyped).
  const handlePeriodChange = (next: string): void => {
    if (isViewMode(next)) {
      setSelectedViewMode(next)
    }
  }

  // Build chart series: each visible metric plus a dashed comparison sibling
  // when comparison data is present, gated by viewMode.
  // @see ../MetricChart/buildChartSeries
  const chartSeries = useMemo(
    () => buildChartSeries(visibleMetrics, viewMode),
    [visibleMetrics, viewMode],
  )

  // Colour authority for the summary swatches: the same positional palette the
  // chart resolves for these metrics, so a dot always matches its line/bar.
  const summaryPaletteIndexByKey = useMemo(
    () => buildMetricPaletteIndex(visibleMetrics.map((m) => m.key)),
    [visibleMetrics],
  )

  // Content Impact draws a composed chart (posts as bars, followers as a line)
  // via its own component; every other view uses the shared MetricLineChart.
  const isContentImpactView = selectedView?.key === 'content-impact'
  // Only the Posts metric draws as a column chart; every other single metric
  // (reach/views counts and followers/net-new) stays a line.
  const isColumnView =
    !isContentImpactView &&
    visibleMetrics.length === 1 &&
    visibleMetrics[0]?.key === 'posts'

  // Compute per-metric summaries for the SummaryPanel.
  const summaryItems = useMemo((): SummaryItem[] => {
    return visibleMetrics.map((metric) => {
      const primarySummary =
        !loading && !error
          ? getTimeSeriesSummary(metric.data, metric.summaryMode)
          : { latest: null }
      const comparisonSummary =
        !loading && !error && metric.comparisonData
          ? getTimeSeriesSummary(metric.comparisonData, metric.summaryMode)
          : null

      // Respect viewMode: surface the correct period value as the main number.
      // "This Period" → primary absolute; "Comparison" → comparison absolute;
      // "Both" → primary absolute (delta shown as main, primary/comparison
      //           used for the "X vs Y in comparison period" sub-text).
      const pv =
        viewMode === ViewMode.Comparison
          ? (comparisonSummary?.latest ?? null)
          : primarySummary.latest
      // cv is only needed in "Both" mode to power the "X vs Y" sub-text.
      // In other modes it would duplicate pv, creating "4 vs 4" nonsense.
      const cv =
        viewMode === ViewMode.Both ? (comparisonSummary?.latest ?? null) : null

      // Period-over-period delta: % for point-in-time metrics (latest or
      // undefined), absolute count for aggregated metrics (total).
      // Note: summaryMode is optional and defaults to 'latest' in
      // getTimeSeriesSummary, so treat undefined the same as 'latest' here.
      const differenceMode: 'percentage' | 'absolute' =
        metric.summaryMode === SummaryMode.Total ? 'absolute' : 'percentage'
      let differenceValue: number | null = null
      if (primarySummary.latest != null && comparisonSummary?.latest != null) {
        if (differenceMode === 'percentage' && comparisonSummary.latest !== 0) {
          differenceValue =
            ((primarySummary.latest - comparisonSummary.latest) /
              Math.abs(comparisonSummary.latest)) *
            100
        } else {
          differenceValue = primarySummary.latest - comparisonSummary.latest
        }
      }

      // Net new is already a change measure — showing a % change of a change
      // produces misleading numbers (e.g. -133% when you lost 1 follower vs
      // gaining 3 last period). Benchmark series have no period comparison
      // (channel vs cohort is always shown as two simultaneous lines).
      // Both cases: show raw value + "vs" line instead of a delta.
      const isBenchmark = metric.key.startsWith('benchmark-')
      // The side-by-side primary + comparison values make the delta self-evident,
      // so no metric shows a computed delta in "Both" mode — always show the
      // raw period value.
      const showDelta = false

      // A bar-drawn series takes the hatched swatch; a line stays dashed.
      const drawsAsBar =
        isColumnView ||
        (isContentImpactView && metric.summaryMode === SummaryMode.Total)
      const comparisonSwatch: 'dashed' | 'hatched' = drawsAsBar
        ? 'hatched'
        : 'dashed'

      return {
        key: metric.key,
        title: metric.title,
        color: metricColorToken(metric.key, summaryPaletteIndexByKey),
        primaryValue: pv,
        comparisonValue: cv,
        differenceValue: showDelta ? differenceValue : null,
        differenceMode,
        // Benchmark growth-rate values are percentages; append '%' after the number.
        unit: isBenchmark ? '%' : undefined,
        comparisonSwatch,
      }
    })
  }, [
    visibleMetrics,
    viewMode,
    loading,
    error,
    summaryPaletteIndexByKey,
    isColumnView,
    isContentImpactView,
  ])

  const hasAnyData = chartSeries.some((m) =>
    m.data.some((point) => point.value != null),
  )

  // True when at least one visible series contains at least one non-zero value.
  // Zero-only data (e.g. a 3-day range with no activity) is treated as
  // "no meaningful data" for insight purposes and shows a no-data notice instead.
  // Checks all visible metrics so that multi-metric views (e.g. Content Impact)
  // don't show a no-data notice when only one of their series has no activity.
  // In "Comparison" mode the user sees comparisonData, so check that instead.
  // Exception: follower metrics are meaningful even at zero — a period with no
  // follower change is valid information (a stable audience), so a present-but-
  // flat series still renders rather than collapsing to a no-data state.
  // @see CORE-6425
  const hasMeaningfulData = visibleMetrics.some((metric) => {
    const data =
      viewMode === ViewMode.Comparison && metric.comparisonData?.length
        ? metric.comparisonData
        : metric.data
    const zeroIsMeaningful =
      metric.key === 'followers' || metric.key === 'followers-net-new'
    return data.some(
      (point) => point.value != null && (zeroIsMeaningful || point.value !== 0),
    )
  })

  // Whether we are currently on the benchmark (follower growth rate) view.
  const isBenchmarkView = selectedView?.key === 'follower-growth-rate'
  // Gate for the period controls, shared by the narrow (dropdown) and wide
  // (toggle) renderings so they can't drift out of sync.
  const showPeriodControl = hasAnyComparison && !isBenchmarkView

  // The flag marks where Buffer began collecting this channel's analytics — the
  // start of every collected series (followers, net-new, reach, views). It shows
  // on any view that plots at least one such series, including Content Impact
  // (posts bars + a followers line). The Posts view is posts-only — those counts
  // come from Buffer's own record of published content, not collected analytics —
  // so it gets no flag. Benchmark is excluded too: it charts an independent
  // 12-month window, not the date-picker range the flag is keyed to.
  const showsDataCollectionFlag =
    !isBenchmarkView && visibleMetrics.some((m) => m.key !== 'posts')

  // Treat an all-zero period the same as an empty one: a flat line pinned to
  // zero isn't a useful chart, so we surface the shared no-data empty state
  // (matching the other Insights blocks) instead of an empty-looking chart plus
  // a separate "no data" notice. Benchmark view is exempt — its cohort median
  // line stays meaningful even when the channel's own rate is zero.
  const showChartEmptyState =
    hasNeverSynced || !hasAnyData || (!isBenchmarkView && !hasMeaningfulData)

  // Reach and profile-views are the only channel metrics Meta gates behind the
  // 100-follower threshold, so the follower-threshold empty state is limited to
  // views that plot only those — the follower/net-new views stay populated.
  // @see CORE-6425
  const isThresholdGatedView =
    visibleMetrics.length > 0 &&
    visibleMetrics.every((m) => m.key === 'reach' || m.key === 'views')

  // Benchmark view charts a fixed 12-month window that ignores the date picker;
  // every other view uses the picker range. Single source of truth shared by
  // the live chart and the "Share as Post" export so the two never diverge.
  const chartStartDate = isBenchmarkView
    ? (benchmarkStartDate ?? startDate)
    : startDate
  const chartEndDate = isBenchmarkView ? (benchmarkEndDate ?? endDate) : endDate

  // Pre-extract the benchmark channel rate and median from summaryItems so
  // BenchmarkSummaryPanel can receive them without re-computing.
  const benchmarkChannelRate = isBenchmarkView
    ? (summaryItems.find((i) => i.key === 'benchmark-growth-rate')
        ?.primaryValue ?? null)
    : null
  const benchmarkMedianRate = isBenchmarkView
    ? (summaryItems.find((i) => i.key === 'benchmark-median')?.primaryValue ??
      null)
    : null

  const {
    message: insightMessage,
    isPositive: insightIsPositive,
    variant: insightVariant,
  } = useInsightMessage({
    visibleMetrics,
    viewMode,
    selectedView,
    singleMetricKey,
    isBenchmarkView,
    benchmarkChannelRate,
    benchmarkMedianRate,
    loading,
    error,
    isInactive,
  })

  // "Share as Post" — reuses the same export hook as the summary panel.
  const primaryMetric = visibleMetrics[0]
  const primaryTitle = primaryMetric?.title ?? selectedView?.title ?? ''

  const exportText = useMemo(() => {
    const label = primaryTitle.toLowerCase()
    const location = channelName ? ` on ${channelName}` : ''
    if (label.includes('follower'))
      return `Check out how my followers have grown${location}! 📊`
    if (label.includes('post'))
      return `Check out how I've been posting${location}! 📊`
    return `Check out my ${primaryTitle}${location} insights! 📊`
  }, [primaryTitle, channelName])

  const primarySummaryValue = useMemo(() => {
    if (!primaryMetric || loading || error) return null
    return getTimeSeriesSummary(primaryMetric.data, primaryMetric.summaryMode)
      .latest
  }, [primaryMetric, loading, error])

  // Benchmark series already carry values as percentage numbers (0.8 = 0.8%);
  // format them with a fixed decimal + '%' instead of the abbreviating
  // `formatNumber`, which would print "0.8" or "1K" depending on magnitude.
  const formattedPrimaryValue =
    primarySummaryValue == null
      ? null
      : primaryMetric?.unit === MetricUnit.Percentage
        ? `${primarySummaryValue.toFixed(2)}%`
        : formatNumber(primarySummaryValue)

  // Shared by "Share as Post" and the bulk snapshot download so both render an
  // identical chart image for the active view.
  const chartCaptureOptions = {
    channelIds,
    text: exportText,
    heading:
      formattedPrimaryValue != null
        ? `${formattedPrimaryValue} ${primaryTitle}`
        : primaryTitle,
    subheading: formatDateRange(chartStartDate, chartEndDate),
    altText: `Buffer Insights chart of ${primaryTitle}${
      channelName ? ` for ${channelName}` : ''
    } over ${formatDateRange(chartStartDate, chartEndDate)}.`,
    channelName,
    channelService,
    channelAvatarUrl,
    // The column view snapshots as vertical bars. The bar snapshot drops the
    // comparison overlay, so pass the primary-period series (matching the
    // heading/date range) — chartSeries in "Comparison" mode is comparison-only
    // and would otherwise leave the snapshot empty.
    chartMetrics: isColumnView ? visibleMetrics : chartSeries,
    chartStacked: isColumnView,
    // Content Impact snapshots as the composed bar (posts) + line (followers)
    // chart so the shared image matches the on-screen view.
    chartComposed: isContentImpactView,
    chartStartDate,
    chartEndDate,
    chartMonthly: isBenchmarkView,
  }

  const { onExport, isExporting } = useExportChartAsPost(chartCaptureOptions)

  const canExport =
    canCreatePosts &&
    Boolean(channelIds) &&
    !loading &&
    !error &&
    visibleMetrics.length > 0

  // A snapshot per available tab — the ZIP covers every view, not just the
  // on-screen tab. Memoized so buildChartSeries doesn't re-run per tab on
  // unrelated re-renders of this (heavy) component.
  const metricsSnapshots = useMemo(
    () =>
      availableViews.map((view, index) => {
        const { options, available } = buildMetricsViewSnapshot(view, {
          metricsWithDerived,
          channelName,
          channelService,
          channelAvatarUrl,
          startDate,
          endDate,
          benchmarkStartDate,
          benchmarkEndDate,
          loading,
          hasError: error != null,
          refreshing,
          hasNeverSynced: Boolean(hasNeverSynced),
          hasChannel: Boolean(channelIds),
        })
        return {
          id: `metrics-${view.key}`,
          filename: `metrics-${view.key}.png`,
          order: 10 + index,
          available,
          getBlob: async (): Promise<Blob> =>
            (await captureChartAsBlob(options)).blob,
        }
      }),
    [
      availableViews,
      metricsWithDerived,
      channelName,
      channelService,
      channelAvatarUrl,
      startDate,
      endDate,
      benchmarkStartDate,
      benchmarkEndDate,
      loading,
      error,
      refreshing,
      hasNeverSynced,
      channelIds,
    ],
  )
  useRegisterSnapshots(metricsSnapshots)

  // Benchmark view uses a fixed 12-month window that ignores all filters.
  // Other views use the date picker range with optional comparison.
  const sectionDateRange = isBenchmarkView
    ? benchmarkStartDate && benchmarkEndDate
      ? formatDateRange(benchmarkStartDate, benchmarkEndDate)
      : 'Last 12 months'
    : formatInsightsDateRange(
        startDate,
        endDate,
        comparisonStartDate,
        comparisonEndDate,
      )

  const comparisonNotice = isBenchmarkView
    ? null
    : getComparisonLengthNotice(
        startDate,
        endDate,
        comparisonStartDate,
        comparisonEndDate,
        selectedDateRange,
      )

  // Tag and post type filters both narrow posts, so they apply only on the views
  // that plot posts metrics (Posts tab and Content Impact). Every other view
  // plots a channel-level collected series — followers, reach, views — which no
  // post filter can narrow. The benchmark view ignores the date range as well.
  const viewPlotsPosts =
    selectedViewKey === 'posts' || selectedViewKey === 'content-impact'
  const activePostFilters = [
    hasTagFilter ? 'tag' : null,
    hasPostTypeFilter ? 'post type' : null,
  ].filter((name): name is string => name != null)
  const inapplicableFilters = viewPlotsPosts ? [] : activePostFilters
  // The benchmark view names tag whether or not one is set, because its warning
  // describes the chart rather than the current selection. Post type joins it
  // only when selected, so a channel without the control never sees it.
  const benchmarkInapplicableFilters = [
    'date',
    'tag',
    ...(hasPostTypeFilter ? ['post type'] : []),
  ]
  const hasNotice =
    isBenchmarkView ||
    Boolean(comparisonNotice || inapplicableFilters.length > 0)

  const sectionSubtitle = hasNotice ? (
    <Text size="sm" color="subtle">
      {sectionDateRange}
      {isBenchmarkView ? (
        <Text as="span" size="sm" color="warning">
          {` · ${inapplicableFiltersNotice(benchmarkInapplicableFilters)}`}
        </Text>
      ) : (
        <>
          {comparisonNotice && (
            <Text as="span" size="sm" color="warning">
              {` · Comparing ${comparisonNotice.primaryDays} vs ${comparisonNotice.comparisonDays} days`}
            </Text>
          )}
          {inapplicableFilters.length > 0 && (
            <Text as="span" size="sm" color="warning">
              {` · ${inapplicableFiltersNotice(inapplicableFilters)}`}
            </Text>
          )}
        </>
      )}
    </Text>
  ) : undefined

  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title as="h3">Metrics</SectionCard.Title>
        <SectionCard.Actions>
          {canExport ? (
            <IconButton
              className={sectionButtonStyles.neutralBackground}
              size="medium"
              onClick={onExport}
              loading={isExporting}
              disabled={refreshing}
              label={
                isExporting ? 'Sharing Metrics…' : 'Share as Post for Metrics'
              }
              tooltip={isExporting ? 'Sharing…' : 'Share as Post'}
            >
              <SendHorizontalIcon />
            </IconButton>
          ) : undefined}
        </SectionCard.Actions>
        <SectionCard.Subtitle>
          {sectionSubtitle ?? sectionDateRange}
        </SectionCard.Subtitle>
      </SectionCard.Header>
      {loading && <Skeleton className={styles.skeletonChart} />}
      {!loading && error && (
        <div className={styles.errorState}>
          <ChartErrorState />
        </div>
      )}
      {!loading && !error && (
        <>
          {/* Controls row: the metric tab bar; on narrow cards the period
              control joins it here as a dropdown (see periodControlNarrow). */}
          <div className={styles.metricsControls}>
            <NarrativeTabBar
              availableViews={availableViews}
              selectedViewKey={selectedViewKey}
              singleMetricKey={singleMetricKey}
              singleMetricTitle={singleMetricTitle}
              singleMetricOptions={singleMetricOptions}
              onViewChange={(key): void => {
                // Update view state first so tracking can never block the switch.
                const isViewChange = key !== selectedViewKey
                setUserSelectedViewKey(key)
                // Track genuine top-level view switches (not no-op re-selects).
                // The 'posts' view is entered by opening the metric dropdown; its
                // selection is tracked on actual pick (onSingleMetricChange), so we
                // don't fire on merely opening it.
                if (isViewChange && key !== 'posts') {
                  trackInsightsItemClicked({
                    page: 'channel-insights',
                    section: 'metrics',
                    element: 'view-tab',
                    item: key,
                    channelIds,
                    serviceTypes: channelService ? [channelService] : [],
                  })
                }
              }}
              onSingleMetricChange={(key): void => {
                // Update state first so tracking can never block the selection.
                const isMetricChange = key !== singleMetricKey
                setSingleMetricKey(key)
                setUserSelectedViewKey('posts')
                // Track genuine single-metric switches in the Posts dropdown.
                if (isMetricChange) {
                  trackInsightsItemClicked({
                    page: 'channel-insights',
                    section: 'metrics',
                    element: 'metric-dropdown',
                    item: key,
                    channelIds,
                    serviceTypes: channelService ? [channelService] : [],
                  })
                }
              }}
            />
            {showPeriodControl && (
              <span className={styles.periodControlNarrow}>
                <SegmentedDropdown
                  value={viewMode}
                  onChange={handlePeriodChange}
                  options={PERIOD_DROPDOWN_OPTIONS}
                />
              </span>
            )}
          </div>

          {/* Narrative chart card */}
          <div
            className={clsx(
              styles.chartWrapper,
              refreshing && styles.refreshing,
            )}
            aria-busy={refreshing}
          >
            {/* Top banner: insight message. Suppressed when the chart shows
                  its empty state — that already communicates the empty period. */}
            {!showChartEmptyState && (
              <InsightNotice
                hasMeaningfulData={hasMeaningfulData}
                isBenchmarkView={isBenchmarkView}
                selectedDateRange={selectedDateRange}
                message={insightMessage}
                isPositive={insightIsPositive}
                variant={insightVariant}
                onReviewTakeaways={onReviewTakeaways}
                isCurrentPeriod={isCurrentPeriod}
              />
            )}
            {/* White card: summary row on top, full-width chart below */}
            <div className={styles.chartCard}>
              {/* Summary metrics (left) + period toggle (right). Hidden when the
                  chart shows its empty state so no misleading zero summary value
                  sits above the empty message. @see CORE-6425 */}
              {!showChartEmptyState && (
                <Flex
                  justify="between"
                  align="start"
                  wrap="wrap"
                  gap="md"
                  className={styles.topSection}
                >
                  {isBenchmarkView ? (
                    <BenchmarkSummaryPanel
                      channelRate={benchmarkChannelRate}
                      medianRate={benchmarkMedianRate}
                      percentileRank={benchmarkPercentileRank}
                    />
                  ) : (
                    <SummaryPanel
                      items={summaryItems}
                      hasComparison={hasAnyComparison}
                      viewMode={viewMode}
                    />
                  )}

                  {showPeriodControl && (
                    // Full toggle on wide cards; on narrow it collapses to the
                    // dropdown in the controls row above (periodControlNarrow).
                    <span className={styles.periodControlWide}>
                      <PeriodToggle
                        value={viewMode}
                        onChange={setSelectedViewMode}
                        startDate={startDate}
                        endDate={endDate}
                        comparisonStartDate={comparisonStartDate}
                        comparisonEndDate={comparisonEndDate}
                      />
                    </span>
                  )}
                </Flex>
              )}
              {/* Full-width chart */}
              <div className={styles.chartContainer}>
                {showChartEmptyState ? (
                  <div className={styles.emptyState}>
                    {hasNeverSynced ? (
                      <NeverSyncedEmptyState />
                    ) : belowFollowerThreshold && isThresholdGatedView ? (
                      <FollowerThresholdEmptyState />
                    ) : (
                      <NoDataEmptyState
                        description={getNoDataDescription(selectedDateRange)}
                        showSuggestion
                        size="small"
                      />
                    )}
                  </div>
                ) : isContentImpactView ? (
                  <ContentImpactChart
                    metrics={chartSeries}
                    startDate={chartStartDate}
                    endDate={chartEndDate}
                    comparisonOnly={viewMode === ViewMode.Comparison}
                    annotations={
                      showsDataCollectionFlag ? annotations : undefined
                    }
                    xAxisTicks={
                      showsDataCollectionFlag ? xAxisTicks : undefined
                    }
                  />
                ) : isColumnView ? (
                  <PostsColumnChart
                    metrics={chartSeries}
                    startDate={chartStartDate}
                    endDate={chartEndDate}
                    comparisonOnly={viewMode === ViewMode.Comparison}
                  />
                ) : (
                  <MetricLineChart
                    metrics={chartSeries}
                    startDate={chartStartDate}
                    endDate={chartEndDate}
                    singleAxis={isBenchmarkView}
                    monthly={isBenchmarkView}
                    comparisonOnly={viewMode === ViewMode.Comparison}
                    // Only carry the data-collection flag on views that plot a
                    // followers line (the metric it explains); see
                    // `showsDataCollectionFlag`.
                    annotations={
                      showsDataCollectionFlag ? annotations : undefined
                    }
                    xAxisTicks={
                      showsDataCollectionFlag ? xAxisTicks : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  )
}
